import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  ShoppingCart, Plus, Minus, X, CreditCard, DollarSign, Check, ChevronDown,
  User, Package, Search, Barcode, Receipt, Printer, Clock, AlertTriangle,
  Save, Trash2, ArrowLeft, ArrowRight, MoreVertical, Eye, Edit, Truck
} from 'lucide-react'
import { cn, formatCurrency, formatNumber } from '../../lib/utils'
import { useQuery } from '@tanstack/react-query'
import { productsApi, posApi, queryKeys } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Modal } from '../../components/ui/Modal'
import { Dropdown } from '../../components/ui/Dropdown'
import { Skeleton } from '../../components/ui/Skeleton'
import { Table } from '../../components/ui/Table'
import { useUIStore } from '../../stores/uiStore'
import { useAuthStore , usePermissions } from '../../stores/authStore'
import toast from 'react-hot-toast'

export default function POSPage() {
  const { hasPermission } = usePermissions()
  const { modals, openModal, closeModal } = useUIStore()
  const [search, setSearch] = useState('')
  const [cart, setCart] = useState<CartItem[]>([])
  const [customer, setCustomer] = useState<string>('')
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'card' | 'mixed'>('cash')
  const [cashReceived, setCashReceived] = useState(0)
  const [shiftOpen, setShiftOpen] = useState(false)

  const { data: products, isLoading: productsLoading } = useQuery({
    queryKey: queryKeys.products({ search, page_size: 50, is_active: true, is_sellable: true }),
    queryFn: () => productsApi.list({ search, page_size: 50, is_active: true, is_sellable: true }),
  })

  const { data: shift } = useQuery({
    queryKey: queryKeys.currentShift(),
    queryFn: () => posApi.getCurrentShift(),
  })

  const addToCart = (product: any, variant?: any) => {
    setCart(prev => {
      const existing = prev.find(item =>
        item.product_id === product.id && item.variant_id === variant?.id
      )
      if (existing) {
        return prev.map(item =>
          item === existing ? { ...item, quantity: item.quantity + 1 } : item
        )
      }
      return [...prev, {
        id: `${product.id}-${variant?.id || 'main'}-${Date.now()}`,
        product_id: product.id,
        variant_id: variant?.id,
        product_name: product.name,
        variant_name: variant?.name,
        sku: variant?.sku || product.sku,
        price: variant?.sale_price || product.sale_price || 0,
        quantity: 1,
        tax_rate: product.tax_rate || 9,
        discount: 0,
      }]
    })
  }

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      setCart(prev => prev.filter(item => item.id !== id))
      return
    }
    setCart(prev => prev.map(item =>
      item.id === id ? { ...item, quantity } : item
    ))
  }

  const removeFromCart = (id: string) => {
    setCart(prev => prev.filter(item => item.id !== id))
  }

  const clearCart = () => setCart([])

  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0)
  const totalDiscount = cart.reduce((sum, item) => sum + item.discount * item.quantity, 0)
  const totalTax = cart.reduce((sum, item) => sum + (item.price * item.quantity - item.discount * item.quantity) * (item.tax_rate / 100), 0)
  const total = subtotal - totalDiscount + totalTax
  const change = paymentMethod === 'cash' ? Math.max(0, cashReceived - total) : 0

  const handleSale = async () => {
    if (cart.length === 0) {
      toast.error('سبد خرید خالی است')
      return
    }
    try {
      await posApi.createSale({
        customer_id: customer || null,
        items: cart.map(item => ({
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
          unit_price: item.price,
          discount: item.discount,
          tax_rate: item.tax_rate,
        })),
        payment_method: paymentMethod,
        cash_received: cashReceived,
      })
      toast.success('فروش با موفقیت ثبت شد')
      clearCart()
      setCustomer('')
      setCashReceived(0)
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'ثبت فروش ناموفق بود')
    }
  }

  const handleShiftOpen = async () => {
    try {
      await posApi.openShift({ opening_cash: 0 })
      toast.success('شیفت باز شد')
      setShiftOpen(true)
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'باز کردن شیفت ناموفق بود')
    }
  }

  const handleShiftClose = async () => {
    try {
      await posApi.closeShift({ closing_cash: 0 })
      toast.success('شیفت بسته شد')
      setShiftOpen(false)
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'بستن شیفت ناموفق بود')
    }
  }

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col">
      {/* Top Bar */}
      <div className="sticky top-16 z-20 bg-white/95 dark:bg-neutral-950/95 backdrop-blur-xl border-b border-neutral-200 dark:border-neutral-800 px-4 py-3">
        <div className="max-w-full mx-auto flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="search"
                placeholder="جستجوی کالا (نام، SKU، بارکد)..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-xl bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 text-sm text-neutral-900 dark:text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Select
              value={paymentMethod}
              onChange={setPaymentMethod}
              options={[
                { value: 'cash', label: 'نقدی' },
                { value: 'card', label: 'کارت' },
                { value: 'mixed', label: 'نقدی + کارت' },
              ]}
              className="w-36"
            />
            {paymentMethod === 'cash' || paymentMethod === 'mixed' ? (
              <Input
                type="number"
                label="دریافتی نقدی"
                placeholder="مبلغ دریافتی"
                value={cashReceived}
                onChange={(e) => setCashReceived(Number(e.target.value))}
                className="w-40"
              />
            ) : null}
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-neutral-100 dark:bg-neutral-800">
              <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">مجموع:</span>
              <span className="text-lg font-bold text-primary-600 dark:text-primary-400 font-mono">{formatCurrency(total)}</span>
            </div>
            {paymentMethod === 'cash' && cashReceived > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-success-100 dark:bg-success-900/30">
                <span className="text-sm font-medium text-success-700 dark:text-success-300">باقی:</span>
                <span className="text-lg font-bold text-success-600 dark:text-success-400 font-mono">{formatCurrency(change)}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Products */}
        <div className="w-full lg:w-2/3 flex flex-col border-l border-neutral-200 dark:border-neutral-800">
          {/* Category Filter */}
          <div className="p-4 border-b border-neutral-200 dark:border-neutral-800 bg-white/50 dark:bg-neutral-950/50">
            <div className="flex flex-wrap gap-2">
              <button className="px-3 py-1.5 rounded-lg text-sm font-medium bg-primary-600 text-white">همه</button>
              {['موبایل', 'لپ‌تاپ', 'لوازم جانبی', 'صوتی', 'شبکه'].map(cat => (
                <button key={cat} className="px-3 py-1.5 rounded-lg text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors">{cat}</button>
              ))}
            </div>
          </div>

          {/* Products Grid */}
          <div className="flex-1 overflow-y-auto p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {productsLoading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="card p-3">
                  <Skeleton variant="rectangular" width="100%" height={80} />
                  <Skeleton variant="text" width="80%" className="mt-2" />
                  <Skeleton variant="text" width="60%" />
                </motion.div>
              ))
            ) : products?.items.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <Package className="w-12 h-12 text-neutral-300 dark:text-neutral-600 mx-auto mb-3" />
                <p className="text-neutral-500 dark:text-neutral-400">کالایی یافت نشد</p>
              </div>
            ) : (
              (products?.items || []).map((product, index) => (
                <motion.button
                  key={product.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.02 }}
                  onClick={() => addToCart(product)}
                  className="card p-3 hover:shadow-lg transition-all text-right group"
                >
                  <div className="aspect-square rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center mb-2 overflow-hidden relative">
                    {product.images?.[0] ? (
                      <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                      <Package className="w-8 h-8 text-neutral-400" />
                    )}
                    {product.current_stock === 0 && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <span className="text-white font-bold text-sm px-2 py-1 rounded bg-danger-500">ناموجود</span>
                      </div>
                    )}
                  </div>
                  <p className="font-medium text-sm text-neutral-900 dark:text-white truncate">{product.name}</p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">{product.sku}</p>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-neutral-200 dark:border-neutral-800">
                    <span className="font-bold text-neutral-900 dark:text-white">{formatCurrency(product.sale_price || 0)}</span>
                    <Badge variant={product.current_stock === 0 ? 'danger' : product.current_stock <= (product.min_stock || 0) ? 'warning' : 'success'} className="text-xs">
                      {product.current_stock}
                    </Badge>
                  </div>
                </motion.button>
              ))
            )}
          </div>
        </div>

        {/* Right Panel - Cart */}
        <div className="w-full lg:w-1/3 flex flex-col bg-white dark:bg-neutral-900 border-r border-neutral-200 dark:border-neutral-800">
          {/* Cart Header */}
          <div className="p-4 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
            <h3 className="font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-primary-600" />
              سبد خرید
              <Badge variant="primary" className="text-xs">{cart.length}</Badge>
            </h3>
            {cart.length > 0 && (
              <Button variant="ghost" size="xs" onClick={clearCart} className="text-danger-600">
                <Trash2 className="w-4 h-4 ml-1" />
                خالی کردن
              </Button>
            )}
          </div>

          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto p-4">
            {cart.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <ShoppingCart className="w-16 h-16 text-neutral-300 dark:text-neutral-600 mb-4" />
                <p className="text-neutral-500 dark:text-neutral-400">سبد خرید خالی است</p>
                <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-1">کالاها را از سمت چپ اضافه کنید</p>
              </div>
            ) : (
              <div className="space-y-3">
                {cart.map((item) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center gap-3 p-3 rounded-xl bg-neutral-50 dark:bg-neutral-800/50"
                  >
                    <div className="w-12 h-12 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center flex-shrink-0">
                      <Package className="w-5 h-5 text-neutral-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-neutral-900 dark:text-white truncate">{item.product_name}</p>
                      {item.variant_name && <p className="text-xs text-neutral-500 dark:text-neutral-400">{item.variant_name}</p>}
                      <p className="text-xs text-neutral-400 dark:text-neutral-500 font-mono">{item.sku}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="w-8 h-8 rounded-lg bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center text-neutral-600 dark:text-neutral-300 hover:bg-neutral-300 dark:hover:bg-neutral-600 transition-colors"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-10 text-center font-medium text-neutral-900 dark:text-white">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-8 h-8 rounded-lg bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center text-neutral-600 dark:text-neutral-300 hover:bg-neutral-300 dark:hover:bg-neutral-600 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="ml-1 p-1.5 rounded-lg text-neutral-400 hover:text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/30 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="text-left w-24">
                      <p className="font-bold text-neutral-900 dark:text-white font-mono text-sm">{formatCurrency(item.price * item.quantity)}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Cart Summary */}
            {cart.length > 0 && (
              <div className="mt-4 pt-4 border-t border-neutral-200 dark:border-neutral-800 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-neutral-600 dark:text-neutral-400">مجموع کالاها</span>
                  <span className="font-medium text-neutral-900 dark:text-white">{formatCurrency(subtotal)}</span>
                </div>
                {totalDiscount > 0 && (
                  <div className="flex justify-between text-sm text-danger-600">
                    <span>تخفیف</span>
                    <span className="font-medium">-{formatCurrency(totalDiscount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm text-neutral-600 dark:text-neutral-400">
                  <span>مالیات ({cart[0]?.tax_rate || 9}%)</span>
                  <span className="font-medium">{formatCurrency(totalTax)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-neutral-200 dark:border-neutral-800">
                  <span className="text-neutral-900 dark:text-white">مجموع قابل پرداخت</span>
                  <span className="text-primary-600 dark:text-primary-400">{formatCurrency(total)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="p-4 border-t border-neutral-200 dark:border-neutral-800 space-y-3">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
                <input type="checkbox" className="w-4 h-4 rounded border-neutral-300 text-primary-600" />
                مشتری عضو باشگاه
              </label>
            </div>

            <Select
              value={customer}
              onChange={setCustomer}
              options={[
                { value: '', label: 'مشتری نقدی' },
                { value: '1', label: 'شرکت آریان تک' },
                { value: '2', label: 'فروشگاه سپهر' },
              ]}
              placeholder="انتخاب مشتری (اختیاری)"
            />

            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => openModal('hold-sale')} disabled={cart.length === 0}>
                <Clock className="w-4 h-4 ml-1" />
                نگهداری
              </Button>
              <Button variant="outline" onClick={() => openModal('return-sale')}>
                <ArrowLeft className="w-4 h-4 ml-1" />
                مرجوعی
              </Button>
            </div>

            <Button
              size="lg"
              className="w-full"
              onClick={handleSale}
              disabled={cart.length === 0 || (paymentMethod === 'cash' && cashReceived < total)}
            >
              <CreditCard className="w-5 h-5 ml-1" />
              {paymentMethod === 'cash' ? 'پرداخت نقدی' : paymentMethod === 'card' ? 'پرداخت کارت' : 'پرداخت ترکیبی'}
            </Button>

            {/* Shift Status */}
            <div className="pt-4 border-t border-neutral-200 dark:border-neutral-800">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">وضعیت شیفت</span>
                <Badge variant={shiftOpen ? 'success' : 'neutral'} className="text-xs">
                  {shiftOpen ? 'باز' : 'بسته'}
                </Badge>
              </div>
              {shiftOpen ? (
                <Button variant="outline" size="sm" className="w-full" onClick={handleShiftClose}>
                  <Clock className="w-4 h-4 ml-1" />
                  بستن شیفت
                </Button>
              ) : (
                <Button variant="primary" size="sm" className="w-full" onClick={handleShiftOpen}>
                  <Clock className="w-4 h-4 ml-1" />
                  باز کردن شیفت
                </Button>
              )}
              {shift && (
                <div className="mt-2 text-xs text-neutral-500 dark:text-neutral-400 space-y-1">
                  <div>شروع: {formatDate(shift.opened_at)}</div>
                  <div>نقدی افتتاحیه: {formatCurrency(shift.opening_cash || 0)}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface CartItem {
  id: string
  product_id: string
  variant_id?: string
  product_name: string
  variant_name?: string
  sku: string
  price: number
  quantity: number
  tax_rate: number
  discount: number
}